import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  MoreVertical,
  AtSign,
  RefreshCw,
  ArrowUp,
  Smile,
  MessageSquare,
  Trash2,
  Check,
  UserCheck,
  UserCog,
  FolderOpen,
  Timer,
  Plus,
  ClipboardCheck,
  ClipboardX,
  AlertCircle,
} from "lucide-react";
import { Notification, NotificationType, HrmNotification } from "types/notifications";
import { getInitials } from "utils/getInitials";
import HrmNotificationCard from "../modules/Notifications/HrmNotificationCard";

// ─── PMO Icon / colour mapping ─────────────────────────────────────────────────

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case "TASK_ASSIGNED":           return UserCheck;
    case "TASK_ASSIGNEE_UPDATED":   return UserCog;
    case "MENTIONED_IN_COMMENT":
    case "MENTIONED_IN_TASK":       return AtSign;
    case "TASK_STATUS_UPDATED":     return RefreshCw;
    case "TASK_PRIORITY_UPDATED":   return ArrowUp;
    case "COMMENT_REACTION":        return Smile;
    case "TIME_LOGGED":             return Timer;
    case "WORK_ITEM_CREATED":       return Plus;
    case "PROJECT_INVITE":          return FolderOpen;
    default:                        return MessageSquare;
  }
};

const getNotificationColors = (type: NotificationType) => {
  switch (type) {
    case "TASK_ASSIGNED":
    case "TASK_ASSIGNEE_UPDATED":   return { bg: "bg-[#9B6DFF]/10", text: "text-[#9B6DFF]" };
    case "MENTIONED_IN_COMMENT":
    case "MENTIONED_IN_TASK":       return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "TASK_STATUS_UPDATED":     return { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]" };
    case "TASK_PRIORITY_UPDATED":   return { bg: "bg-[#EF4444]/10", text: "text-[#EF4444]" };
    case "COMMENT_REACTION":        return { bg: "bg-[#EC4899]/10", text: "text-[#EC4899]" };
    case "TIME_LOGGED":             return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
    case "WORK_ITEM_CREATED":       return { bg: "bg-[#10B981]/10", text: "text-[#10B981]" };
    case "PROJECT_INVITE":          return { bg: "bg-[#9B6DFF]/10", text: "text-[#9B6DFF]" };
    default:                        return { bg: "bg-[#3B82F6]/10", text: "text-[#3B82F6]" };
  }
};

// ─── HRM Icon / colour mapping ─────────────────────────────────────────────────

// ─── Date grouping ────────────────────────────────────────────────────────────

const getDateGroup = (createdAt: string): "Today" | "Yesterday" | "Older" => {
  const now = new Date();
  const date = new Date(createdAt);
  const todayStr = now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === todayStr) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return "Older";
};

const formatNotificationTimestamp = (createdAt: string, group: "Today" | "Yesterday" | "Older"): string => {
  const date = new Date(createdAt);

  if (group === "Today") {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours || 1}h ago`;
  }

  if (group === "Yesterday") {
    return "Yesterday";
  }

  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};


const NotificationMessage: React.FC<{ item: Notification }> = ({ item }) => {
  const toTitleCase = (str: string): string => {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (item.notificationType === "TASK_STATUS_UPDATED") {
    const statusRegex = /(.*) updated status of (.+?) from (.+) to (?!.* to )(.+)/i;
    const match = item.notificationMessage.match(statusRegex);
    if (match) {
      const oldStatus = match[3].trim();
      const newStatus = match[4].trim();
      const titleDisplay = item.title || match[2];
      return (
        <div className="flex flex-col gap-0.5">
          <p className="text-gray-200 text-[13px] font-semibold leading-snug break-words">
            {titleDisplay}
          </p>
          <p className="text-gray-300 text-xs leading-snug break-words">
            Status changed by {item.actorName || match[1]}
          </p>
          <p className="text-gray-400 text-xs italic leading-snug break-words">
            {toTitleCase(oldStatus)} &rarr; {toTitleCase(newStatus)}
          </p>
        </div>
      );
    }
  }

  if (item.notificationType === "WORK_ITEM_CREATED") {
    // Backend sends "<actor> added a sub-item (<childId>) under <parentId>: <title>"
    // — drop the redundant child-item-id parenthetical and say "child item".
    const subItemRegex = /^(.+?) added a sub-item \([^)]*\) under (.+)$/i;
    const match = item.notificationMessage.match(subItemRegex);
    if (match) {
      const actor = match[1].trim();
      const rest = match[2].trim();
      return (
        <div className="flex flex-col gap-0.5">
          {item.title && (
            <p className="text-gray-200 text-[13px] font-semibold leading-snug break-words">
              {item.title}
            </p>
          )}
          <p className="text-gray-300 text-xs leading-snug break-words">
            {actor} added a child item under {rest}
          </p>
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col gap-0.5">
      {item.title && (
        <p className="text-gray-200 text-[13px] font-semibold leading-snug break-words">
          {item.title}
        </p>
      )}
      <p className="text-gray-300 text-xs leading-snug break-words">
        {item.notificationMessage}
      </p>
    </div>
  );
};

// ─── Actor avatar ─────────────────────────────────────────────────────────────

const ActorAvatar: React.FC<{ notification: Notification; size?: number }> = ({
  notification,
  size = 32,
}) => {
  const style = { width: size, height: size, minWidth: size };

  if (notification.actorProfileImageUrl) {
    return (
      <img
        src={notification.actorProfileImageUrl}
        alt={notification.actorName}
        style={style}
        className="rounded-full object-cover shrink-0"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      style={style}
      className="rounded-full bg-[#9B6DFF]/20 text-[#9B6DFF] flex items-center justify-center text-[11px] font-bold shrink-0"
    >
      {getInitials(notification.actorName || "?")}
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const NotificationSkeleton: React.FC = () => (
  <div className="flex gap-3 px-4 py-3 animate-pulse">
    <div className="w-8 h-8 rounded-full bg-white/10 shrink-0" />
    <div className="flex-1 space-y-2 pt-1">
      <div className="h-3 bg-white/10 rounded w-4/5" />
      <div className="h-2.5 bg-white/10 rounded w-2/5" />
    </div>
  </div>
);

// ─── HRM Notification Card (Extracted) ─────────────────────────────────────────

// ─── Props ────────────────────────────────────────────────────────────────────

export interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  // PMO
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  onRemove: (uuid: string, e: React.MouseEvent) => void;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onNotificationClick: (notification: Notification) => void;
  onViewAll?: () => void;
  // HRM
  hrmNotifications: HrmNotification[];
  hrmUnreadCount: number;
  isLoadingHrm: boolean;
  onHrmRemove: (uuid: string) => void;
  onHrmMarkAllRead: () => void;
  onHrmClearAll: () => void;
  onHrmNotificationClick: (notification: HrmNotification) => void;
  onHrmViewAll?: () => void;
  hasPmoAccess?: boolean;
  hasHrmsAccess?: boolean;
}

type ActiveTab = "PMO" | "HRM";

// ─── Component ────────────────────────────────────────────────────────────────

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  isLoading,
  onRemove,
  onMarkAllRead,
  onClearAll,
  onNotificationClick,
  onViewAll,
  hrmNotifications,
  hrmUnreadCount,
  isLoadingHrm,
  onHrmRemove,
  onHrmMarkAllRead,
  onHrmClearAll,
  onHrmNotificationClick,
  onHrmViewAll,
  hasPmoAccess = true,
  hasHrmsAccess = true,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const defaultTab = hasPmoAccess ? "PMO" : "HRM";
  const [activeTab, setActiveTab] = useState<ActiveTab>(defaultTab);

  // useEffect(() => {
  //   if (!hasPmoAccess && activeTab === "PMO") {
  //     setActiveTab("HRM");
  //   } else if (!hasHrmsAccess && activeTab === "HRM") {
  //     setActiveTab("PMO");
  //   }
  // }, [hasPmoAccess, hasHrmsAccess, activeTab]);

  useEffect(() => {
  if (!hasPmoAccess && !hasHrmsAccess) return; // nowhere valid to switch to — bail out
  if (!hasPmoAccess && activeTab === "PMO" && hasHrmsAccess) {
    setActiveTab("HRM");
  } else if (!hasHrmsAccess && activeTab === "HRM" && hasPmoAccess) {
    setActiveTab("PMO");
  }
}, [hasPmoAccess, hasHrmsAccess, activeTab]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Current tab data ───────────────────────────────────────────────────────
  const isPmo = activeTab === "PMO";
  const currentNotifications = isPmo ? notifications : [];
  const currentHrmNotifications = isPmo ? [] : hrmNotifications;
  const currentUnread = isPmo ? unreadCount : hrmUnreadCount;
  const currentLoading = isPmo ? isLoading : isLoadingHrm;

  // Group PMO notifications client-side
  const pmoGroups = (["Today", "Yesterday", "Older"] as const).map((label) => ({
    label,
    items: currentNotifications.filter((n) => n && n.createdAt && getDateGroup(n.createdAt) === label),
  })).filter((g) => g.items.length > 0);

  // Group HRM notifications client-side
  const hrmGroups = (["Today", "Yesterday", "Older"] as const).map((label) => ({
    label,
    items: currentHrmNotifications.filter((n) => n && n.createdAt && getDateGroup(n.createdAt) === label),
  })).filter((g) => g.items.length > 0);

  const badgeLabel =
    currentUnread === 0 ? null : currentUnread >= 10 ? "9+" : String(currentUnread);

  const isEmpty = isPmo
    ? currentNotifications.length === 0
    : currentHrmNotifications.length === 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="fixed top-16 left-4 right-4 sm:absolute sm:top-full sm:right-0 sm:left-auto sm:w-[400px] sm:mt-3 bg-primary-light rounded-xl shadow-2xl border border-primary-subtle overflow-hidden z-50 flex flex-col max-h-[calc(100vh-100px)] sm:max-h-[580px]"
        >
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className="relative z-20 flex items-center justify-between px-4 py-3 border-b border-[#2A2F3D] shrink-0 bg-primary-variant/90 backdrop-blur-md">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-white text-base">Notifications</h3>
              {badgeLabel && (
                <span className="text-[10px] font-bold bg-[#9B6DFF]/20 text-[#a78bfa] px-2 py-0.5 rounded-full">
                  {badgeLabel} new
                </span>
              )}
            </div>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className={`p-1.5 rounded-lg text-gray-400 hover:text-white transition-colors ${
                  menuOpen ? "bg-white/10 text-white" : "hover:bg-white/5"
                }`}
              >
                <MoreVertical size={16} />
              </button>

              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.1 }}
                    className="absolute right-0 mt-1 w-48 bg-primary-variant border border-primary-subtle rounded-lg shadow-xl py-1 z-[60]"
                  >
                    <button
                      onClick={() => {
                        isPmo ? onMarkAllRead() : onHrmMarkAllRead();
                        setMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-white/5 hover:text-white flex items-center gap-2"
                    >
                      <Check size={14} className="text-gray-400" />
                      Mark all as read
                    </button>
                    <button
                      onClick={() => {
                        isPmo ? onClearAll() : onHrmClearAll();
                        setMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 flex items-center gap-2 border-t border-primary-subtle"
                    >
                      <Trash2 size={14} />
                      Clear all notifications
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ── Module Tabs ──────────────────────────────────────────────────── */}
          {hasPmoAccess && hasHrmsAccess && (
            <div className="flex border-b border-[#2A2F3D] bg-primary-variant/60 shrink-0">
              {(["PMO", "HRM"] as ActiveTab[])
                .filter(tab => (tab === "PMO" && hasPmoAccess) || (tab === "HRM" && hasHrmsAccess))
                .map((tab) => {
                const tabUnread = tab === "PMO" ? unreadCount : hrmUnreadCount;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`relative flex-1 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                      activeTab === tab
                        ? "text-white border-b-2 border-[#9B6DFF]"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    {tab}
                    {tabUnread > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-[#EF4444] text-white">
                        {tabUnread >= 10 ? "9+" : tabUnread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── List ───────────────────────────────────────────────────────── */}
          <div className="overflow-y-auto flex-1 py-1 bg-primary-variant/30 scrollbar-thin scrollbar-thumb-gray-800">
            {currentLoading && isEmpty ? (
              <>
                <NotificationSkeleton />
                <NotificationSkeleton />
                <NotificationSkeleton />
              </>
            ) : isEmpty ? (
              <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-3">
                  <Bell size={20} className="text-gray-500" />
                </div>
                <p className="text-white font-medium text-xs">All caught up!</p>
                <p className="text-gray-400 text-[10px] mt-1">No new notifications at this time.</p>
              </div>
            ) : isPmo ? (
              <>
                {pmoGroups.map(({ label, items }) => (
                  <div key={label} className="flex flex-col">
                    <div className="text-[#9ca3b0] text-[10px] uppercase font-bold tracking-wider px-4 pt-3 pb-1.5">
                      {label}
                    </div>
                    {items.map((item) => {
                      const IconComponent = getNotificationIcon(item.notificationType);
                      const iconColors = getNotificationColors(item.notificationType);
                      return (
                        <div
                          key={item.uuid}
                          role="button"
                          tabIndex={0}
                          onClick={() => onNotificationClick(item)}
                          onKeyDown={(e) => e.key === "Enter" && onNotificationClick(item)}
                          className={`relative flex gap-3 px-4 py-3 transition-all duration-150 group border-b border-primary-subtle last:border-b-0 cursor-pointer ${
                            item.isRead ? "hover:bg-white/3" : "bg-[#9B6DFF]/5 hover:bg-[#9B6DFF]/10"
                          }`}
                        >
                          <div className="relative shrink-0">
                            <ActorAvatar notification={item} size={34} />
                            <div
                              className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-primary-variant ${iconColors.bg} ${iconColors.text}`}
                            >
                              <IconComponent size={10} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0 pr-8">
                            <NotificationMessage item={item} />
                            <span className="text-[11px] text-gray-500 mt-1 block">
                              {formatNotificationTimestamp(item.createdAt, label)}
                            </span>
                          </div>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-end h-6 min-w-6">
                            {!item.isRead && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#9B6DFF] group-hover:opacity-0 transition-opacity" />
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); onRemove(item.uuid, e); }}
                              className="absolute right-0 opacity-0 group-hover:opacity-100 flex items-center justify-center p-1.5 rounded-lg border border-[#2A2F3D] text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/20 transition-all duration-150 shadow-sm"
                              title="Delete notification"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>
            ) : (
              <>
                {hrmGroups.map(({ label, items }) => (
                  <div key={label} className="flex flex-col">
                    <div className="text-[#9ca3b0] text-[10px] uppercase font-bold tracking-wider px-4 pt-3 pb-1.5">
                      {label}
                    </div>
                    {items.map((item) => (
                      <HrmNotificationCard
                        key={item.uuid}
                        item={item}
                        onRemove={onHrmRemove}
                        onClick={onHrmNotificationClick}
                        isDropdownMode={true}
                      />
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          <div className="flex flex-col border-t border-[#2A2F3D] bg-primary-variant/90 backdrop-blur-md">
            {((isPmo && onViewAll) || (!isPmo && onHrmViewAll)) && (
              <button
                onClick={isPmo ? onViewAll : onHrmViewAll}
                className="py-3 text-center text-xs font-semibold text-white hover:text-[#9B6DFF] hover:bg-white/5 transition-all duration-150 shrink-0"
              >
                View all notifications
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
